/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms/views/View'
import { mono, boFont, boMedia, boMotion } from './backoffice.tokens'

export interface BoStatTile {
	label: string
	value: React.ReactNode
	/** 값 아래 작은 줄 */
	note?: React.ReactNode
	/** 있으면 타일이 버튼이 된다 — 홈 「할 일」 이 그 조건의 목록을 연다 */
	onClick?: () => void
}

interface PropsType {
	items: BoStatTile[]
	className?: string
	/** 폭에 맞춰 열 수를 정한다(최소 128px, 데스크톱 콘텐츠 폭에서 7개가 한 줄) — 타일이 4개보다 많은 줄(홈 「할 일」) */
	dense?: boolean
}

/** 큰 수 타일 — 데스크톱 4열 · 태블릿 3열 · 폰 2열 (RESPONSIVE_PLAN §7) */
export const BoStatTiles = (props: PropsType) => (
	<View
		css={style}
		className={`boStatTiles ${props.dense ? 'dense' : ''} ${props.className ?? ''}`}
	>
		{props.items.map((item, i) =>
			item.onClick !== undefined ? (
				<button
					type="button"
					className="boStatTile boStatTileBtn"
					key={i}
					onClick={item.onClick}
				>
					<span className="boStatTileLabel">{item.label}</span>
					<span className="boStatTileValue">{item.value}</span>
					{item.note !== undefined ? (
						<span className="boStatTileNote">{item.note}</span>
					) : null}
				</button>
			) : (
				<View className="boStatTile" key={i}>
					<span className="boStatTileLabel">{item.label}</span>
					<span className="boStatTileValue">{item.value}</span>
					{item.note !== undefined ? (
						<span className="boStatTileNote">{item.note}</span>
					) : null}
				</View>
			),
		)}
	</View>
)

const style = css({
	display: 'grid',
	gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
	gap: 24,
	fontFamily: boFont.ui,

	'.boStatTile': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		minWidth: 0,
	},
	'.boStatTileLabel': {
		fontSize: 11,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},
	'.boStatTileValue': {
		fontSize: 34,
		fontWeight: 200,
		lineHeight: 1,
		letterSpacing: '-0.01em',
		fontVariantNumeric: 'tabular-nums',
		color: mono.ink,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	'.boStatTileNote': { fontSize: 12, color: mono.dim, fontWeight: 300 },
	/** 클릭 타일 — 버튼 기본 외형을 지우고 hover 에서 값만 진해진다. 색·레이아웃 이동 없음 */
	'.boStatTileBtn': {
		appearance: 'none',
		background: 'transparent',
		border: 0,
		padding: 0,
		margin: 0,
		font: 'inherit',
		color: 'inherit',
		textAlign: 'left',
		cursor: 'pointer',
		'.boStatTileValue, .boStatTileLabel': { transition: boMotion },
		'&:hover .boStatTileLabel': { color: mono.dim },
		'&:hover .boStatTileValue': {
			textDecoration: 'underline',
			textDecorationThickness: 1,
			textUnderlineOffset: 6,
		},
		'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 4 },
	},

	'&.dense': {
		gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))',
		gap: '20px 20px',
	},

	[boMedia.tablet]: {
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: 20,
	},
	[boMedia.phone]: {
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 16,
		'.boStatTileValue': { fontSize: 26 },
	},
})
